import { Injectable } from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { TCreatedPdf } from 'pdfmake/build/pdfmake';
import {
  CustomTableLayout,
  TDocumentDefinitions,
  TFontDictionary,
} from 'pdfmake/interfaces';

import {
  VaccinePdfAddress,
  VaccinePdfRecord,
  VaccinesPdfBuildResult,
  VaccinesPdfDocumentDefinition,
  VaccinesPdfInput,
} from './vaccines.models';

interface PdfMakeWithFonts {
  vfs?: Record<string, string>;
  fonts?: TFontDictionary;
  createPdf(
    documentDefinition: TDocumentDefinitions,
    tableLayouts?: Record<string, CustomTableLayout>,
    fonts?: TFontDictionary,
    vfs?: Record<string, string>,
  ): TCreatedPdf;
}

interface PdfFontsModule {
  pdfMake?: { vfs?: Record<string, string> };
  vfs?: Record<string, string>;
}

const vaccinePdfMake = pdfMake as unknown as PdfMakeWithFonts;
const vaccinePdfFonts = pdfFonts as unknown as PdfFontsModule;

vaccinePdfMake.vfs = vaccinePdfFonts.pdfMake?.vfs ?? vaccinePdfFonts.vfs ?? {};
vaccinePdfMake.fonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf',
  },
};

@Injectable({ providedIn: 'root' })
export class VaccinesPdfService {
  build(input: VaccinesPdfInput): VaccinesPdfBuildResult {
    return {
      documentDefinition: this.buildDocument(input),
      filename: this.resolveFilename(input),
    };
  }

  buildDocument(input: VaccinesPdfInput): VaccinesPdfDocumentDefinition {
    const vaccines = input.vaccines
      .filter(
        (vaccine) =>
          Boolean(vaccine.vaccineNameRaw || vaccine.vaccineName) &&
          vaccine.status?.trim().toLowerCase() !== 'scheduled',
      )
      .sort(
        (a, b) =>
          new Date(b.vaccinationDate ?? 0).getTime() -
          new Date(a.vaccinationDate ?? 0).getTime(),
      );

    if (!vaccines.length) {
      return { content: [] };
    }

    const showNextDue = vaccines.some(
      (vaccine) => vaccine.reVaccinationDate != null,
    );
    const ownerName = [input.owner.firstname, input.owner.lastname]
      .filter(Boolean)
      .join(' ');
    const ownerPhone = this.formatPhoneNumber(
      input.owner.phoneNumbers?.[0]?.phoneNumber ?? '',
    );
    const ownerAddress = input.owner.address ?? input.owner.mailingAddress;
    const ownerCityLine = this.formatCityLine(ownerAddress);
    const shelterAddressLine = this.formatAddress(input.shelter.mailingAddress);
    const shelterPhone = this.formatPhoneNumber(
      input.shelter.phoneNumber ?? '',
    );
    const petName = this.getPetName(input);
    const latestWeight = input.pet.weightHistory?.at(-1)?.weightLbs;
    const weight =
      latestWeight != null
        ? input.convertWeight(
            latestWeight,
            input.sourceWeightUnit,
            input.weightUnit,
          )
        : '';
    const microchip = this.formatMicrochip(
      input.pet.microChip,
      input.pet.microChipIssuer,
    );
    const headers: any[] = [
      {
        text: 'Vaccinations',
        bold: true,
        style: 'header2',
        margin: [0, 5],
        color: '#033557',
        font: 'Roboto',
      },
      {
        text: 'Vaccinated On',
        bold: true,
        style: 'header2',
        margin: [0, 5],
        color: '#033557',
        font: 'Roboto',
      },
    ];
    const columnWidths = ['*', '*'];

    if (showNextDue) {
      headers.push({
        text: 'Next Due',
        bold: true,
        style: 'header2',
        margin: [0, 5],
        color: '#033557',
        font: 'Roboto',
      });
      columnWidths.push('*');
    }

    return {
      content: [
        {
          text: 'Vaccine Records',
          style: 'title',
          margin: [0, 0, 0, 13.5],
          font: 'Roboto',
          color: '#033557',
        },
        {
          text: input.shelter.businessName ?? '',
          style: 'header',
          margin: [0, 0, 0, 2],
          font: 'Roboto',
          color: '#033557',
        },
        {
          text: shelterAddressLine,
          style: 'address',
          margin: [0, 0, 0, 2],
          font: 'Roboto',
          color: '#033557',
        },
        {
          text: shelterPhone,
          style: 'phone',
          margin: [0, 0, 0, 8.5],
          font: 'Roboto',
          color: '#033557',
        },
        {
          columns: [
            {
              width: '40%',
              stack: [
                {
                  text: 'Owner Info',
                  style: 'subTitleText',
                  margin: [0, 0, 0, 3],
                  font: 'Roboto',
                  color: '#033557',
                },
                this.createDivider(180),
                {
                  margin: [0, 3, 0, 0],
                  layout: 'noBorders',
                  table: {
                    body: [
                      [
                        {
                          text: ownerName,
                          fontSize: 10,
                          style: 'ownerAnimalText',
                          font: 'Roboto',
                          color: '#2E5874',
                        },
                      ],
                      [
                        {
                          text: input.owner.email ?? '',
                          fontSize: 10,
                          style: 'ownerAnimalText',
                          font: 'Roboto',
                          color: '#2E5874',
                        },
                      ],
                      [
                        {
                          text: ownerPhone,
                          fontSize: 10,
                          style: 'ownerAnimalText',
                          font: 'Roboto',
                          color: '#2E5874',
                        },
                      ],
                      [
                        {
                          text: ownerAddress?.streetLine1 ?? '',
                          fontSize: 10,
                          style: 'ownerAnimalText',
                          font: 'Roboto',
                          color: '#2E5874',
                        },
                      ],
                      [
                        {
                          text: ownerAddress?.streetLine2 ?? '',
                          fontSize: 10,
                          style: 'ownerAnimalText',
                          font: 'Roboto',
                          color: '#2E5874',
                        },
                      ],
                      [
                        {
                          text: ownerCityLine,
                          fontSize: 10,
                          style: 'ownerAnimalText',
                          font: 'Roboto',
                          color: '#2E5874',
                        },
                      ],
                    ],
                  },
                },
              ],
            },
            {
              width: '60%',
              stack: [
                {
                  text: 'Animal Info',
                  style: 'subTitleText',
                  margin: [0, 0, 0, 3],
                  font: 'Roboto',
                  color: '#033557',
                },
                this.createDivider(250),
                {
                  margin: [0, 3, 0, 0],
                  layout: 'noBorders',
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        this.createInfoCell('Name', petName),
                        this.createInfoCell('Gender', input.pet.sex),
                      ],
                      [
                        this.createInfoCell('Altered', input.pet.altered),
                        this.createInfoCell('Species', input.pet.animalType),
                      ],
                      [
                        this.createInfoCell('Breed', input.pet.breed),
                        this.createInfoCell('Color', input.pet.color),
                      ],
                      [
                        this.createInfoCell(
                          'DOB',
                          this.formatDate(input.pet.dateOfBirth),
                        ),
                        this.createInfoCell('Weight', weight),
                      ],
                    ],
                  },
                },
                {
                  layout: 'noBorders',
                  table: {
                    body: [
                      [
                        {
                          text: microchip,
                          fontSize: 10,
                          style: 'ownerAnimalText',
                          font: 'Roboto',
                          color: '#2E5874',
                        },
                      ],
                    ],
                  },
                },
              ],
            },
          ],
        },
        {
          margin: [0, 10, 0, 0],
          stack: [
            {
              text: 'Vaccine History',
              style: 'subTitleText',
              margin: [0, 0, 0, 3],
              font: 'Roboto',
              color: '#033557',
            },
          ],
        },
        {
          margin: [0, 3, 0, 0],
          table: {
            widths: columnWidths,
            body: [
              headers,
              ...vaccines.map((vaccine) =>
                this.createVaccineRow(vaccine, showNextDue),
              ),
            ],
          },
          layout: {
            fillColor: (rowIndex: number) =>
              rowIndex === 0 ? '#e7ecf3' : null,
            hLineWidth: (index: number, node: any) =>
              index === node.table.body.length ? 0 : 1,
            lineHeight: (row: number) => (row === 1 ? 2 : 1),
            vLineWidth: () => 0,
            hLineColor: () => '#CCD6DD',
            vLineColor: () => '#CCD6DD',
          },
        },
      ],
      footer: {
        text: this.getFormattedDateAndTime(),
        style: 'footer',
        font: 'Roboto',
        color: '#2E5874',
      },
      defaultStyle: {
        font: 'Roboto',
        color: '#2E5874',
      },
      styles: {
        ownerAnimalText: {
          fontSize: 10,
          color: '#2E5874',
          font: 'Roboto',
        },
        title: {
          fontSize: 16,
          bold: true,
          alignment: 'center',
          font: 'Roboto',
          color: '#033557',
        },
        header: {
          fontSize: 15,
          alignment: 'center',
          font: 'Roboto',
          color: '#033557',
        },
        address: {
          fontSize: 9,
          alignment: 'center',
          font: 'Roboto',
          color: '#033557',
        },
        phone: {
          fontSize: 9,
          alignment: 'center',
          font: 'Roboto',
          color: '#033557',
        },
        name: {
          fontSize: 20,
          bold: true,
          font: 'Roboto',
          color: '#033557',
        },
        subTitleText: {
          fontSize: 16,
          bold: true,
          font: 'Roboto',
          color: '#033557',
        },
        header2: {
          fontSize: 14,
          bold: true,
          font: 'Roboto',
          color: '#033557',
          fillColor: '#e7ecf3',
        },
        vaccineText: {
          fontSize: 10,
          font: 'Roboto',
          color: '#2E5874',
        },
        footer: {
          fontSize: 10,
          alignment: 'center',
          font: 'Roboto',
          color: '#2E5874',
        },
      },
    };
  }

  download(input: VaccinesPdfInput): void {
    const result = this.build(input);
    this.downloadDocument(result.documentDefinition, result.filename);
  }

  downloadDocument(
    documentDefinition: VaccinesPdfDocumentDefinition,
    filename: string,
  ): void {
    if (!documentDefinition.content.length) {
      return;
    }

    vaccinePdfMake
      .createPdf(documentDefinition as TDocumentDefinitions)
      .download(filename);
  }

  private createVaccineRow(
    vaccine: VaccinePdfRecord,
    includeNextDue: boolean,
  ): any[] {
    const row: any[] = [
      {
        text: vaccine.vaccineNameRaw || vaccine.vaccineName || '-',
        margin: [0, 5, 30, 0],
        style: 'vaccineText',
        font: 'Roboto',
        color: '#2E5874',
      },
      {
        text: this.formatDate(vaccine.vaccinationDate),
        margin: [0, 5, 30, 0],
        style: 'vaccineText',
        font: 'Roboto',
        color: '#2E5874',
      },
    ];

    if (includeNextDue) {
      row.push({
        text: this.formatDate(vaccine.reVaccinationDate),
        margin: [0, 5, 30, 0],
        style: 'vaccineText',
        font: 'Roboto',
        color: '#2E5874',
      });
    }

    return row;
  }

  private createDivider(width: number): any {
    return {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: width,
          y2: 0,
          lineWidth: 1,
          lineColor: '#CCD6DD',
        },
      ],
    };
  }

  private createInfoCell(
    label: string,
    value: string | boolean | null | undefined,
  ): any {
    return {
      text: `${label}: ${value ?? ''}`,
      fontSize: 10,
      style: 'ownerAnimalText',
      font: 'Roboto',
      color: '#2E5874',
    };
  }

  private getPetName(input: VaccinesPdfInput): string {
    const name = input.pet.name?.trim() ?? '';
    const sourceId = input.shelterAnimalSourceDisplayId?.trim();
    return sourceId ? `${name} (${sourceId})` : name;
  }

  private resolveFilename(input: VaccinesPdfInput): string {
    const suppliedFilename = input.filename?.trim();

    if (suppliedFilename) {
      return suppliedFilename.toLowerCase().endsWith('.pdf')
        ? suppliedFilename
        : `${suppliedFilename}.pdf`;
    }

    const petName = this.getPetName(input);
    return petName ? `${petName} Vaccine Records.pdf` : 'VaccineRecords';
  }

  private formatDate(value: string | Date | null | undefined): string {
    if (!value) return '-';

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime()) || date.getFullYear() <= 1) {
      return '-';
    }

    return date
      .toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
      .replaceAll('/', '-');
  }

  private formatPhoneNumber(phone: string, showCountryCode = false): string {
    if (!phone) return '';

    const normalized = phone.replaceAll(' ', '').replaceAll('-', '');

    if (normalized.length === 10) {
      return normalized.replace(/^(\d{0,3})(\d{0,3})(\d{0,4})/, '($1) $2-$3');
    }

    if (normalized.length > 10) {
      const number = normalized.slice(-10);
      const countryCode = normalized.slice(0, -10);
      const prefix =
        showCountryCode && countryCode !== '+1' ? `${countryCode} ` : '';

      return `${prefix}${number.replace(
        /^(\d{0,3})(\d{0,3})(\d{0,4})/,
        '($1) $2-$3',
      )}`;
    }

    return normalized;
  }

  private formatAddress(address?: VaccinePdfAddress | null): string {
    if (!address) return '';

    return [
      address.streetLine1,
      address.streetLine2,
      address.city,
      address.stateProvince,
      address.zipCode,
    ]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(', ');
  }

  private formatCityLine(address?: VaccinePdfAddress | null): string {
    if (!address) return '';

    return [address.city, address.stateProvince, address.zipCode]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(', ');
  }

  private formatMicrochip(
    microchip: string | null | undefined,
    issuer: string | null | undefined,
  ): string {
    if (!microchip && !issuer) return '';

    const chipText = microchip ? `Microchip: ${microchip}` : 'Microchip:';
    return issuer ? `${chipText} (${issuer})` : chipText;
  }

  private getFormattedDateAndTime(): string {
    const now = new Date();
    const date = now.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    const time = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${date} | ${time} Powered by Petszel`;
  }
}
